import { _isNotEmpty, _notNil } from '@/littledash';
import type {
  SendDeclarationStatus,
  SendRunScope,
  SendScopeReason,
  SendTrcProxy,
  SendTrialSummaryDeclaration,
} from '@/model/Agent.model';

/** A declaration's values as the study gave them, quoted, or nothing when it gave none. */
const quoted = (declaration: SendTrialSummaryDeclaration): string | undefined =>
  _isNotEmpty(declaration.declared_values)
    ? declaration.declared_values.map((value) => `"${value}"`).join(' and ')
    : undefined;

/** Why the terminology release used is not the one the study declared, when it isn't. */
const undeclaredBecause = (declaration: SendTrialSummaryDeclaration): string => {
  const values = quoted(declaration);
  switch (declaration.status) {
    case 'no_trial_summary':
      return 'no TS was uploaded to declare one';
    case 'not_declared':
      return 'the study declares none';
    case 'unreadable':
      return `the study's declaration${_notNil(values) ? ` ${values}` : ''} couldn't be read`;
    case 'conflicting':
      return `the study declares more than one${_notNil(values) ? `: ${values}` : ''}`;
    default:
      return `the study declares ${declaration.version ?? values ?? 'another release'}`;
  }
};

/**
 * The controlled terminology a run used, against what the study declared and what this build bundles.
 * The report's own version field names the release bundled by default, which a run whose terminology
 * checks stood down never used, so this reads the run's scope instead.
 */
export const terminologyText = (scope: SendRunScope): string => {
  const declaration = scope.controlled_terminology;
  const bundled = `bundled: ${declaration.supported_versions.join(', ')}`;
  const used = scope.controlled_terminology_used;
  if (_notNil(used)) {
    return declaration.status === 'supported' && declaration.version === used
      ? `${used} used, as the study declares (${bundled})`
      : `${used} used, the latest bundled, because ${undeclaredBecause(declaration)} (${bundled})`;
  }
  if (scope.state === 'out_of_scope') {
    return `None used: the study is out of scope, so nothing was checked (${bundled})`;
  }
  return `None used: ${undeclaredBecause(declaration)}, which this build doesn't bundle, so the terminology checks stood down (${bundled})`;
};

/** Why a run is not in scope, one sentence per reason the server gave. */
export const SCOPE_REASON_TEXT: Record<SendScopeReason, string> = {
  ts_not_uploaded:
    'No TS (Trial Summary) dataset was uploaded, so nothing says which SENDIG version the study was built to.',
  sendig_version_not_declared: "The study's TS doesn't declare a SENDIG version (SNDIGVER).",
  sendig_version_unreadable: "The study's TS declares a SENDIG version (SNDIGVER) that couldn't be read.",
  sendig_version_conflicting: "The study's TS declares more than one SENDIG version (SNDIGVER).",
  sendig_version_unsupported: 'The study was built to a SENDIG version this validator doesn’t support.',
  ct_version_not_declared: "The study's TS doesn't declare a terminology release (SNDCTVER).",
  ct_version_unreadable: "The study's TS declares a terminology release (SNDCTVER) that couldn't be read.",
  ct_version_conflicting: "The study's TS declares more than one terminology release (SNDCTVER).",
};

/** What one TS declaration said, against what this build supports. */
export const DECLARATION_STATUS_TEXT: Record<SendDeclarationStatus, string> = {
  supported: 'Supported',
  unsupported: 'Not supported',
  recorded: 'Recorded only',
  unreadable: "Couldn't be read",
  conflicting: 'More than one value',
  not_declared: 'Not declared',
  no_trial_summary: 'No TS uploaded',
};

export const DECLARATION_LABEL: Record<SendTrialSummaryDeclaration['parameter'], string> = {
  SNDIGVER: 'SENDIG version',
  SNDCTVER: 'Terminology release',
  DFXMLVER: 'Define-XML version',
  STSTDTC: 'Study start date',
};

/** A run's four TS declarations, in the order the report lists them. */
export const declarationsOf = (scope: SendRunScope): Array<SendTrialSummaryDeclaration> => [
  scope.sendig,
  scope.controlled_terminology,
  scope.define_xml,
  scope.study_start_date,
];

/** A declaration's values as uploaded, quoted, or what stands in for none. */
export const declaredText = (declaration: SendTrialSummaryDeclaration): string =>
  quoted(declaration) ?? DECLARATION_STATUS_TEXT[declaration.status];

export const TRC_OUTCOME_TEXT: Record<SendTrcProxy['outcome'], string> = {
  met: 'Met',
  not_met: 'Not met',
  not_assessed: 'Not assessed',
};

/** Where a scope is shown: after the run, or at the confirm step, before anything is checked. */
export type SendScopeStage = 'report' | 'confirm';

/** The headline for a run's scope state, which says what the run did, or will do, about it. */
export const scopeHeadlineOf = (scope: SendRunScope, stage: SendScopeStage = 'report'): string => {
  const supported = scope.sendig.supported_versions.map((version) => `SENDIG ${version}`).join(' or ');
  switch (scope.state) {
    case 'out_of_scope':
      return stage === 'confirm'
        ? `This study can't be validated: this validator supports ${supported} only`
        : `Nothing was checked: this validator supports ${supported} only`;
    case 'scope_unknown':
      return stage === 'confirm'
        ? `The study's own SENDIG version isn't known, so it will be checked against ${supported}`
        : `Checked against ${supported}, but the study's own SENDIG version isn't known`;
    default:
      // The version read from the study's own TS, never the list this validator supports: the two
      // agree for a study in scope, but only the first is what the study declares.
      return `In scope: the study declares SENDIG ${scope.sendig.version ?? declaredText(scope.sendig)}`;
  }
};
